import React, { useState, useEffect, useCallback, useRef } from 'react';
import { noticeAPI } from '../../api/client';
import { X } from 'lucide-react';
import { Button } from '../ui/button';

const NoticeModal = () => {
  const [notices, setNotices] = useState([]);
  const [currentNotice, setCurrentNotice] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shownInSession, setShownInSession] = useState(new Set());
  const hasFetched = useRef(false);
  const intervalCheckRef = useRef(null);

  const fetchNotices = useCallback(async () => {
    try {
      const response = await noticeAPI.getActiveNotices();
      if (response.data && response.data.length > 0) {
        setNotices(response.data);
      } else {
        setNotices([]);
      }
    } catch (error) {
      console.error('Failed to fetch notices:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchNotices();
    }
  }, [fetchNotices]);

  // Set up interval to periodically check for notices (for time-based showing)
  useEffect(() => {
    // Check every 30 seconds for time-based notices
    intervalCheckRef.current = setInterval(() => {
      if (!currentNotice) {
        fetchNotices();
      }
    }, 30000); // 30 seconds

    return () => {
      if (intervalCheckRef.current) {
        clearInterval(intervalCheckRef.current);
      }
    };
  }, [currentNotice, fetchNotices]);

  useEffect(() => {
    if (notices.length === 0 || currentNotice) return;

    // Find a notice that:
    // 1. Has time-based interval and is eligible (backend already filters)
    // 2. OR hasn't been shown in this session (session-based)
    const noticeToShow = notices.find(n => {
      // If notice has time-based interval, backend already checked eligibility
      if (n.show_again_after_hours && n.show_again_after_hours > 0) {
        return true; // Backend already filtered, so show it
      }
      // Session-based: check if shown in current session
      return !shownInSession.has(n.id);
    });
    
    if (noticeToShow) {
      // Show notice after the configured delay
      const delay = (noticeToShow.show_delay_seconds || 3) * 1000;
      
      const timer = setTimeout(() => {
        setCurrentNotice(noticeToShow);
        setIsAnimating(true);
        // Small delay for fade-in animation
        setTimeout(() => setIsVisible(true), 50);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [notices, currentNotice, shownInSession]);

  const handleClose = async () => {
    if (!currentNotice) return;

    // Start fade-out animation
    setIsVisible(false);
    
    // Mark as viewed in backend
    try {
      await noticeAPI.markViewed(currentNotice.id);
    } catch (error) {
      console.error('Failed to mark notice as viewed:', error);
    }

    // Add to shown in session (for session-based notices)
    if (!currentNotice.show_again_after_hours) {
      setShownInSession(prev => new Set([...prev, currentNotice.id]));
    }

    // Wait for animation then clear current notice
    setTimeout(() => {
      setIsAnimating(false);
      setCurrentNotice(null);
      
      // Refetch to check for remaining eligible notices
      fetchNotices();
    }, 300);
  };

  if (!isAnimating || !currentNotice) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        isVisible ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 z-10 rounded-full bg-white/80 hover:bg-white shadow-md"
          onClick={handleClose}
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Notice Content */}
        {currentNotice.content_type === 'image' ? (
          <div className="relative">
            <img 
              src={currentNotice.content} 
              alt={currentNotice.title}
              className="w-full h-auto max-h-[70vh] object-contain"
            />
            {/* Title overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <h2 className="text-white font-semibold text-lg font-bangla">{currentNotice.title}</h2>
              
              {/* Optional Button for Image Notice */}
              {currentNotice.link_url && currentNotice.link_text && (
                <a 
                  href={currentNotice.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 px-6 py-2.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-bangla"
                  data-testid="notice-link-button"
                >
                  {currentNotice.link_text}
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 pr-8">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-lg">📢</span>
              </div>
              <div>
                <h2 className="font-semibold text-lg text-gray-900 font-bangla">{currentNotice.title}</h2>
                <p className="text-xs text-gray-500 font-bangla">গুরুত্বপূর্ণ বিজ্ঞপ্তি</p>
              </div>
            </div>
            
            {/* Content */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed font-bangla">
                {currentNotice.content}
              </p>
            </div>

            {/* Optional Link Button */}
            {currentNotice.link_url && currentNotice.link_text && (
              <div className="mb-4 flex justify-center">
                <a 
                  href={currentNotice.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-bangla"
                  data-testid="notice-link-button"
                >
                  {currentNotice.link_text}
                </a>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end">
              <Button onClick={handleClose} className="px-6 font-bangla">
                বুঝেছি!
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeModal;
